/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format, isBefore } from 'date-fns';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { validation } from '@medflex/commons';
import Button from '@medflex/components/stories/Button';
import Spinner from '@medflex/components/stories/Spinner';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import InputField from '@medflex/components/stories/InputField';
import InputRadio from '@medflex/components/stories/InputRadio';
import Textarea from 'react-textarea-autosize';
import TextSnippetsPopup from '@medflex/components/stories/TextSnippetsPopup';
import { Absence as absenceConstants } from '@medflex/shared-constants';
import AbsenceEditDateField from './AbsenceEditDateField';
import AbsenceEditTimeField from './AbsenceEditTimeField';

const build = {
  initialValues({ startAt, endAt, allowNotifications, ...rest } = {}) {
    return {
      start: {
        date: startAt ? format(startAt, 'DD.MM.YYYY') : '',
        time: startAt ? format(startAt, 'HH:mm') : '00:00',
      },
      end: {
        date: endAt ? format(endAt, 'DD.MM.YYYY') : '',
        time: endAt ? format(endAt, 'HH:mm') : '23:59',
      },
      allowNotifications: allowNotifications?.toString(),
      ...rest,
    };
  },
  normalizeValues({ start, end, ...rest }) {
    return {
      startAt: new Date(`${start.date.split('.').reverse().join('-')}T${start.time}`).toISOString(),
      endAt: new Date(`${end.date.split('.').reverse().join('-')}T${end.time}`).toISOString(),
      ...rest,
    };
  },
};

const testIsGivenDateInPast = value => {
  const date = new Date(value.split('.').reverse().join('-'));
  return !isBefore(date, new Date(new Date().setHours(0, 0, 0, 0)));
};

const AbsenceEdit = ({ absence, snippets, onSubmit, onAbort }) => {
  const { t } = useTranslation('physician', { keyPrefix: 'InquiryManagementModule.Absence.form' });

  const initialFormValues = useMemo(() => build.initialValues(absence), [absence]);
  const formSchema = yup.object().shape(
    {
      name: yup.string().trim().required(t('errors.name_required')),
      start: yup.object().when('end', (end, _, start) => {
        const startDate = new Date(
          `${start.value?.date?.split('.').reverse().join('-')}T${start.value.time}`,
        );
        const endDate = new Date(`${end.date?.split('.').reverse().join('-')}T${end.time}`);
        const isStartDateAfterEndDate =
          !Number.isNaN(startDate.getTime()) &&
          !Number.isNaN(endDate.getTime()) &&
          startDate.getTime() !== endDate.getTime() &&
          startDate.getTime() > endDate.getTime();

        return yup.object({
          date: yup
            .string()
            .test('', t('errors.date_start_greater_end'), () => !isStartDateAfterEndDate)
            .required(t('errors.date_required')),
          time: yup
            .string()
            .test('', t('errors.date_invalid'), () => !Number.isNaN(startDate.getTime()))
            .test('', t('errors.date_start_greater_end'), () => !isStartDateAfterEndDate)
            .required(''),
        });
      }),
      end: yup
        .object()
        .shape()
        .when('start', (__, _, end) => {
          const { parent } = end;
          const startDate = new Date(
            `${parent.start?.date?.split('.').reverse().join('-')}T${parent.start?.time}`,
          );
          const endDate = new Date(
            `${parent.end?.date?.split('.').reverse().join('-')}T${parent.end?.time}`,
          );

          const isEndDateBeforeStartDate =
            !Number.isNaN(startDate.getTime()) &&
            !Number.isNaN(endDate.getTime()) &&
            startDate.getTime() !== endDate.getTime() &&
            endDate.getTime() < startDate.getTime();

          return yup.object({
            date: yup
              .string()
              .test('', t('errors.date_end_smaller_start'), () => !isEndDateBeforeStartDate)
              .test('', t('errors.date_past'), testIsGivenDateInPast)
              .required(t('errors.date_required')),
            time: yup
              .string()
              .test('', t('errors.date_invalid'), () => !Number.isNaN(endDate.getTime()))
              .test('', t('errors.date_end_smaller_start'), () => !isEndDateBeforeStartDate)
              .required(''),
          });
        }),
      allowNotifications: yup.boolean().required(t('errors.allow_notifications_required')),
      note: yup
        .string()
        .trim()
        .test('', t('errors.note_invalid_string'), validation.isSafeString)
        .required(t('errors.note_required')),
    },
    [
      ['end', 'start'],
      ['start', 'end'],
    ],
  );

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: initialFormValues,
  });

  const {
    register,
    errors,
    handleSubmit: wrapSubmit,
    setValue,
    getValues,
    formState,
  } = formMethods;

  const handleTextSnippetSelection = useCallback(
    snippet => {
      setValue('note', `${getValues('note') || ''} ${snippet.text}`, { shouldValidate: true });
    },
    [getValues, setValue],
  );

  const handleSubmit = useCallback(
    formData => onSubmit(build.normalizeValues(formData)),
    [onSubmit],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-col space-y-6">
        <div>
          <label htmlFor="name" className="font-semibold mb-2">
            {t('name.label')}
          </label>
          <InputField
            name="name"
            ref={register}
            maxLength={absenceConstants.MAX_CHARS_NAME}
            placeholder={t('name.placeholder')}
            state={errors.name ? 'invalid' : 'default'}
            defaultValue={initialFormValues.name}
          />
          {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
        </div>
        <div className="flex space-x-4 lg:space-x-14 justify-start">
          <div className="flex-1">
            <label htmlFor="startDate" className="block font-semibold mb-2">
              {t('start_label')}
            </label>
            <div className="flex flex-col space-y-2 lg:space-y-0 lg:flex-row lg:space-x-4">
              <AbsenceEditDateField name="start.date" id="startDate" />
              <AbsenceEditTimeField
                name="start.time"
                id="startTime"
                className="flex-initial min-w-24"
              />
            </div>
            {!formState.isValid && (errors.start?.date || errors.start?.time) && (
              <p className="text-xs text-red-600">
                {errors.start?.date?.message || errors.start?.time?.message}
              </p>
            )}
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block font-semibold mb-2">
              {t('end_label')}
            </label>
            <div className="flex flex-col space-y-2 lg:space-y-0 lg:flex-row lg:space-x-4">
              <AbsenceEditDateField name="end.date" id="endDate" />
              <AbsenceEditTimeField
                name="end.time"
                id="endTime"
                className="flex-initial min-w-24"
              />
            </div>
            {!formState.isValid && (errors.end?.date || errors.end?.time) && (
              <p className="text-xs text-red-600">
                {errors.end?.date?.message || errors.end?.time?.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col space-y-2">
          <div className="font-semibold mb-2">{t('allow_notifications.label')}</div>
          <label htmlFor="allowNotificatoins_yes" className="flex space-x-2">
            <InputRadio
              id="allowNotificatoins_yes"
              ref={register}
              name="allowNotifications"
              value="true"
            />
            <span>{t('allow_notifications.yes')}</span>
          </label>
          <label htmlFor="allowNotificatoins_no" className="flex space-x-2">
            <InputRadio
              id="allowNotificatoins_no"
              ref={register}
              name="allowNotifications"
              value="false"
            />
            <Trans parent="span" t={t} i18nKey="allow_notifications.no">
              Do <b>not</b> allow messages/requests. Show absence notice.
            </Trans>
          </label>
          {errors.allowNotifications && (
            <p className="text-xs text-red-600">{errors.allowNotifications.message}</p>
          )}
        </div>
        <div>
          <label>
            <div className="font-semibold mb-2">{t('note.label')}</div>
            <Textarea
              className="w-full p-3 resize-none border border-slate-400 focus:border-blue-500 rounded outline-none placeholder-slate-500 leading-tight"
              name="note"
              minRows={3}
              maxRows={10}
              ref={register}
              maxLength={absenceConstants.MAX_CHARS_NOTE}
            />
          </label>
          {errors.note && <p className="text-xs text-red-600">{errors.note.message}</p>}
          <TextSnippetsPopup snippets={snippets} onClick={handleTextSnippetSelection} />
        </div>
        <div className="flex space-x-8 items-center justify-end">
          {!formState.isSubmitting && (
            <Button kind="link" onClick={onAbort}>
              {t('abort')}
            </Button>
          )}
          <Button type="submit" isDisabled={!formState.isValid || formState.isSubmitting}>
            {formState.isSubmitting ? <Spinner /> : t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

AbsenceEdit.propTypes = {
  absence: PropTypes.shape({
    name: PropTypes.string,
    startAt: PropTypes.string,
    endAt: PropTypes.string,
    note: PropTypes.string,
    allowNotifications: PropTypes.bool,
  }),
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      id: PropTypes.string,
    }),
  ),
  onSubmit: PropTypes.func.isRequired,
  onAbort: PropTypes.func.isRequired,
};

AbsenceEdit.defaultProps = {
  absence: {
    name: '',
    note: '',
    startAt: '',
    endAt: '',
  },
};

export default AbsenceEdit;
